import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarDays, FileText, ListChecks } from 'lucide-react'
import { DOC_TEMPLATE_IDS, buildDocTemplate } from '../utils/docTemplates'
import type { Editor } from '@tiptap/core'
import type { DocTemplateId } from '../utils/docTemplates'

const TEMPLATE_ICONS: Record<DocTemplateId, typeof FileText> = {
  spec: ListChecks,
  meetingNotes: CalendarDays,
  plan: FileText,
}

/** Whether the editor has no content yet, kept in sync as you type. */
function useEditorIsEmpty(editor: Editor | null): boolean {
  const [isEmpty, setIsEmpty] = useState(() => editor?.isEmpty ?? false)
  useEffect(() => {
    if (!editor) return
    const sync = () => setIsEmpty(editor.isEmpty)
    sync()
    editor.on('update', sync)
    return () => {
      editor.off('update', sync)
    }
  }, [editor])
  return isEmpty
}

/** Where to start typing after a template: the first empty block, under the first heading. */
function firstEmptyBlockPos(editor: Editor): number {
  const positions: Array<number> = []
  editor.state.doc.descendants((node, pos) => {
    if (positions.length > 0) return false
    if (node.isTextblock && node.content.size === 0) positions.push(pos + 1)
    return true
  })
  return positions[0] ?? 1
}

/**
 * Templates as ghost chips under an empty doc (design 32a). Picking one fills
 * the doc with its outline; they go away as soon as the doc has content.
 */
export function DocStarter({ editor }: { editor: Editor | null }) {
  const { t } = useTranslation('projects')
  const isEmpty = useEditorIsEmpty(editor)

  if (!editor || !isEmpty || !editor.isEditable) return null

  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid="doc-starter">
      {DOC_TEMPLATE_IDS.map((id) => {
        const Icon = TEMPLATE_ICONS[id]
        return (
          <button
            key={id}
            type="button"
            onClick={() => {
              editor.commands.setContent(buildDocTemplate(id, t))
              editor.chain().focus().setTextSelection(firstEmptyBlockPos(editor)).run()
            }}
            className="flex h-7 items-center gap-1.5 rounded-md border border-dashed border-border px-2.5 text-[12.5px] text-muted-foreground transition-colors hover:border-solid hover:bg-secondary hover:text-foreground"
          >
            <Icon className="size-3.5" />
            {t(`docs.templates.${id}.name`)}
          </button>
        )
      })}
    </div>
  )
}
